import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createAnonymousSession, refreshAnonymousSession, resetAnonymousSession, getAuthHeader, resolveOrganizationIdForAnon } from '../services/anonymousAuth.service';

export interface AnonymousAuthState {
  accessToken: string | null;
  expiresAt?: number; // epoch ms
  sessionId?: string;
  organization_id?: string;
  project?: { id: number; name?: string } | undefined;
  loading: boolean;
  error: string | null;
}

export interface AnonymousAuthContextValue extends AnonymousAuthState {
  ensureSession: () => Promise<void>;
  resetSession: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  requestWithAutoRefresh: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  getAuthHeaders: () => HeadersInit;
}

const AnonymousAuthContext = createContext<AnonymousAuthContextValue | undefined>(undefined);

export const AnonymousAuthProvider: React.FC<{ children: React.ReactNode; autoEnsure?: boolean }> = ({ children, autoEnsure = true }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | undefined>(undefined);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [organization_id, setOrganizationId] = useState<string | undefined>(undefined);
  const [project, setProject] = useState<{ id: number; name?: string } | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const inflight = useRef<Promise<void> | null>(null);
  const refreshInflight = useRef<Promise<void> | null>(null);
  const refreshTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRef = useRef<string | null>(null);


  const ensureSession = useCallback(async () => {
    if (inflight.current) return inflight.current;
    inflight.current = (async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to reuse/refresh session first
        try {
          const refreshed = await refreshAnonymousSession();
          tokenRef.current = refreshed.accessToken;
          setAccessToken(refreshed.accessToken);
          if (refreshed.expiresIn) setExpiresAt(Date.now() + refreshed.expiresIn * 1000);
          setSessionId(refreshed.sessionId);
          if (refreshed.organization_id) setOrganizationId(refreshed.organization_id);
          if (refreshed.projectId) setProject({ id: refreshed.projectId, name: refreshed.projectName || undefined });
          return;
        } catch (e: any) {
          // If refresh fails due to 401/403, fall through to create
          const status = e?.status;
          if (status && status !== 401 && status !== 403) throw e;
        }

        // Create a new session
        const orgId = await resolveOrganizationIdForAnon();
        const created = await createAnonymousSession(orgId);
        tokenRef.current = created.accessToken;
        setAccessToken(created.accessToken);
        if (created.expiresIn) setExpiresAt(Date.now() + created.expiresIn * 1000);
        setSessionId(created.sessionId);
        setOrganizationId(created.organization_id || orgId);
        if (created.projectId) setProject({ id: created.projectId, name: created.projectName || undefined });
      } catch (e: any) {
        setError(e?.message || 'Failed to initialize anonymous session');
        setAccessToken(null);
        setExpiresAt(undefined);
        setSessionId(undefined);
        setProject(undefined);
      } finally {
        setLoading(false);
        inflight.current = null;
      }
    })();
    return inflight.current;
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (refreshInflight.current) return refreshInflight.current;
    refreshInflight.current = (async () => {
      try {
        const refreshed = await refreshAnonymousSession();
        // Update token synchronously for immediate reuse
        tokenRef.current = refreshed.accessToken;
        setAccessToken(refreshed.accessToken);
        if (refreshed.expiresIn) setExpiresAt(Date.now() + refreshed.expiresIn * 1000);
        setSessionId(refreshed.sessionId);
        if (refreshed.organization_id) setOrganizationId(refreshed.organization_id);
        if (refreshed.projectId) setProject({ id: refreshed.projectId, name: refreshed.projectName || undefined });
      } finally {
        refreshInflight.current = null;
      }
    })();
    return refreshInflight.current;
  }, []);

  const resetSessionHandler = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reset = await resetAnonymousSession();
      setAccessToken(reset.accessToken);
      if (reset.expiresIn) setExpiresAt(Date.now() + reset.expiresIn * 1000);
      setSessionId(reset.sessionId);
      setOrganizationId(reset.organization_id ?? organization_id);
    } catch (e: any) {
      setError(e?.message || 'Failed to reset session');
    } finally {
      setLoading(false);
    }
  }, [organization_id]);

  useEffect(() => {
    if (autoEnsure) {
      ensureSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEnsure]);

  // Scheduled refresh ~60s before expiry
  useEffect(() => {
    if (refreshTimerId.current) {
      clearTimeout(refreshTimerId.current);
      refreshTimerId.current = null;
    }
    if (!expiresAt) return;
    const now = Date.now();
    const leadMs = 60_000; // refresh 60s before expiry
    const delay = Math.max(0, expiresAt - now - leadMs);
    refreshTimerId.current = setTimeout(() => {
      refreshAccessToken().catch(() => {
        // If refresh fails, try to re-ensure session as a fallback
        ensureSession();
      });
    }, delay);
    return () => {
      if (refreshTimerId.current) {
        clearTimeout(refreshTimerId.current);
        refreshTimerId.current = null;
      }
    };
  }, [expiresAt, refreshAccessToken, ensureSession]);

  const requestWithAutoRefresh = useCallback(async (input: RequestInfo | URL, init?: RequestInit) => {
    const doFetch = async () => {
      // Always rebuild headers to include the latest token from ref (avoids stale closure)
      const currentToken = tokenRef.current || undefined;
      const authHeaders = getAuthHeader(currentToken) as Record<string, string>;
      const mergedHeaders: HeadersInit = {
        ...(init?.headers || {}),
        ...authHeaders,
      };
      return fetch(input, { ...init, headers: mergedHeaders });
    };

    let res = await doFetch();
    if (res.status === 401) {
      try {
        await refreshAccessToken();
        res = await doFetch();
      } catch (_) {
        // Fall back to ensureSession if desired by caller; here we just return the 401
      }
    }
    return res;
  }, [refreshAccessToken]);

  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);

  const value = useMemo<AnonymousAuthContextValue>(() => ({
    accessToken,
    expiresAt,
    sessionId,
    organization_id,
    project,
    loading,
    error,
    ensureSession,
    resetSession: resetSessionHandler,
    refreshAccessToken,
    requestWithAutoRefresh,
    getAuthHeaders: () => getAuthHeader(accessToken || undefined),
  }) as AnonymousAuthContextValue, [accessToken, expiresAt, sessionId, organization_id, project, loading, error, ensureSession, resetSessionHandler, refreshAccessToken, requestWithAutoRefresh]);

  return (
    <AnonymousAuthContext.Provider value={value}>
      {children}
    </AnonymousAuthContext.Provider>
  );
};

export function useAnonymousAuthContext(): AnonymousAuthContextValue {
  const ctx = useContext(AnonymousAuthContext);
  if (!ctx) throw new Error('useAnonymousAuthContext must be used within AnonymousAuthProvider');
  return ctx;
}
